import {countBy} from 'lodash';
import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {FrontEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {Dictionary} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {getHasOmniChannelLinkedConversations} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {useLinkConversationBulkActionPermission} from '../../../../../../srcCommon/permissions/useBulkActionPermission';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {
  canConversationsBeLinked,
  ConversationsLinkCheckErrorsEnum,
  learnMoreErrorUrl,
} from '../../../../../helpers/models/linkedConversationsHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {bulkLinkConversationsAsyncAction} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsActions';
import {navigateToConversation} from '../../../../../store/stateless/navigationActions';
import {PermissionDropdownItem} from '../../../../common/dropdowns/permissionDropdownItem';
import {ConversationsMoreDropdownPublicProps} from '../actions/more/conversationsMoreDropdown';
import {maybeRenderConversationPreview} from './linkConversationsHelpers';

/*
 * Props.
 */

export interface LinkConversationsDropdownItemProps extends ConversationsMoreDropdownPublicProps {
  isInMultiConversationsViewer?: boolean;
  onLinkConversationsClick: () => void;
  selectedConversations: ReadonlyArray<ConversationBaseModel>;
}

/*
 * Styles.
 */

const StyledTextContainerDiv = styled.div`
  max-width: 252px; // for a max width of 300px including other quote + icon
  display: flex;
`;

const StyledEllipsisTextDiv = styled.div`
  ${ellipsis()};
`;

const StyledLinkButton = styled(ChromelessButton)`
  color: inherit;
  text-decoration: underline;
  font-weight: ${fontWeights.semibold};
  display: inline;
`;

/*
 * Component.
 */

export const LinkConversationsMoreDropdownItem: FC<LinkConversationsDropdownItemProps> = (props) => {
  const {selectedConversations, onlyConversation} = props;
  const theme = useUserTheme();
  const {trackDataEvent} = useDataEventTracking();
  const localize = useLocalize();

  const dispatch = useAppDispatch();
  const itemRef = useRef<HTMLDivElement>(null);
  const [shouldShowPreview, toggleShouldShowPreview] = useToggle();
  const hasOmniChannelLinkedConversations = useAppSelector(getHasOmniChannelLinkedConversations);

  const linkCheckError = canConversationsBeLinked(selectedConversations, hasOmniChannelLinkedConversations);
  const linkCounts = countBy(selectedConversations, 'linkStatus');
  const originalConversation = findOriginalConvo(selectedConversations, linkCounts);
  const bulkActionPermission = useLinkConversationBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  const shouldRenderOriginalConversation =
    originalConversation && linkCheckError === ConversationsLinkCheckErrorsEnum.NONE && !onlyConversation;

  if (onlyConversation && onlyConversation?.linkStatus === LinkedConversationStatusesEnum.ORIGINAL) {
    return null;
  }

  const renderLinkConversationsText = () => {
    if (onlyConversation) {
      return (
        <StyledTextContainerDiv>
          <StyledEllipsisTextDiv>
            <Trans id="conversation-actions-link-conversation-to-original">Link to conversation</Trans>
          </StyledEllipsisTextDiv>
        </StyledTextContainerDiv>
      );
    }

    if (shouldRenderOriginalConversation) {
      return (
        <StyledTextContainerDiv>
          <StyledEllipsisTextDiv>
            <Trans id="conversation-actions-link-conversation-to">Link conversation to</Trans> &quot;
            {originalConversation.subject}
          </StyledEllipsisTextDiv>
          &quot;
        </StyledTextContainerDiv>
      );
    }

    return <Trans id="conversation-actions-linked-conversation">Link conversations</Trans>;
  };

  const hasError = linkCheckError !== ConversationsLinkCheckErrorsEnum.NONE;

  const onLearnMoreClick = (event: FrontEvent) => {
    openInBrowser(learnMoreErrorUrl);
  };

  const computeTooltipErrorText = () => {
    switch (linkCheckError) {
      case ConversationsLinkCheckErrorsEnum.MORE_THAN_ONE_PARENT_OR_CHILD:
        return (
          <Trans id="link-error-multiple-linked">
            Some selected conversations have different original conversations. Conversations must link to the
            same original conversation.
          </Trans>
        );
      case ConversationsLinkCheckErrorsEnum.TOO_MANY_CONVERSATIONS:
        return <Trans id="link-error-too-many">Cannot link more than 20 conversation at a time.</Trans>;
      case ConversationsLinkCheckErrorsEnum.UNSUPPORTED_CHANNEL:
        return <Trans id="link-error-channel">Only Email conversations can be linked.</Trans>;
      default:
        return null;
    }
  };

  const renderLinkErrorTooltip = () => {
    const errorTooltipText = computeTooltipErrorText();
    if (!errorTooltipText) {
      return null;
    }

    return (
      <>
        {errorTooltipText}{' '}
        <StyledLinkButton onClick={onLearnMoreClick}>
          <Trans id="link-error-learnmore">Learn more</Trans>
        </StyledLinkButton>
      </>
    );
  };

  const onLinkConversationsClick = async () => {
    trackDataEvent(DataEventActionsEnum.CLICK, LinkedConversationsDataEventObject.BULK_LINK_CONVERSATION, {
      hasOriginalConversationSelected: Boolean(originalConversation),
    });

    if (originalConversation) {
      const selectedConversationIds = selectedConversations
        .map((c) => c.id)
        .filter((id) => id !== originalConversation.id);
      await dispatch(
        bulkLinkConversationsAsyncAction(originalConversation.id, selectedConversationIds, localize),
      );
      await dispatch(navigateToConversation(originalConversation.id));
      trackDataEvent(
        DataEventActionsEnum.SUCCESS,
        LinkedConversationsDataEventObject.BULK_LINK_CONVERSATION,
        {
          sourceConversationIds: selectedConversationIds,
          targetConversationId: originalConversation.id,
        },
      );
    } else {
      props.onLinkConversationsClick();
    }
  };

  if (shouldRenderOriginalConversation) {
    return (
      <div ref={itemRef} onMouseEnter={toggleShouldShowPreview} onMouseLeave={toggleShouldShowPreview}>
        <PermissionDropdownItem
          isDisabled={hasError}
          renderTooltip={hasError ? renderLinkErrorTooltip : undefined}
          tooltipMaxWidth={240}
          tooltipPersistent
          onSelect={onLinkConversationsClick}
          bulkActionPermission={bulkActionPermission}
        >
          <DropdownItemIcon
            label={renderLinkConversationsText()}
            iconSize={16}
            color={theme.greys.shade80}
            iconName="linkedConversationsSmall"
          />
          <DropdownItemContent>{renderLinkConversationsText()}</DropdownItemContent>
        </PermissionDropdownItem>
        {maybeRenderConversationPreview(props.panelId, shouldShowPreview, itemRef, originalConversation)}
      </div>
    );
  }

  return (
    <PermissionDropdownItem
      isDisabled={hasError}
      renderTooltip={hasError ? renderLinkErrorTooltip : undefined}
      tooltipMaxWidth={240}
      tooltipPersistent
      onSelect={onLinkConversationsClick}
      bulkActionPermission={bulkActionPermission}
    >
      <DropdownItemIcon
        label={renderLinkConversationsText()}
        iconSize={16}
        color={theme.greys.shade80}
        iconName="linkedConversationsSmall"
      />
      <DropdownItemContent>{renderLinkConversationsText()}</DropdownItemContent>
    </PermissionDropdownItem>
  );
};

/*
 * Helpers.
 */

function findOriginalConvo(
  selectedConversations: ReadonlyArray<ConversationBaseModel>,
  linkCounts: Dictionary<number>,
) {
  // When 1 original is selected and all unlinked
  if (
    linkCounts[LinkedConversationStatusesEnum.ORIGINAL] === 1 &&
    linkCounts[LinkedConversationStatusesEnum.LINKED] === undefined
  ) {
    return selectedConversations.find(
      (conversation) => conversation.linkStatus === LinkedConversationStatusesEnum.ORIGINAL,
    );
  }

  return undefined;
}
